import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { lazy, Suspense } from "react";

// Lazy load all pages for better performance
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Vagas = lazy(() => import("./pages/Vagas"));
const MinhasVagas = lazy(() => import("./pages/MinhasVagas"));
const CandidaturasRecebidas = lazy(() => import("./pages/CandidaturasRecebidas"));
const MinhasCandidaturas = lazy(() => import("./pages/MinhasCandidaturas"));
const Talentos = lazy(() => import("./pages/Talentos"));
const Empresas = lazy(() => import("./pages/Empresas"));
const Buscar = lazy(() => import("./pages/Buscar"));
const MeusCurriculos = lazy(() => import("./pages/MeusCurriculos"));
const Auth = lazy(() => import("./pages/Auth"));
const Cadastro = lazy(() => import("./pages/Cadastro"));
const Sobre = lazy(() => import("./pages/Sobre"));
const VagaDetalhes = lazy(() => import("./pages/VagaDetalhes"));
const TalentoDetalhes = lazy(() => import("./pages/TalentoDetalhes"));
const Termos = lazy(() => import("./pages/Termos"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Contatos = lazy(() => import("./pages/Contatos"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const FAQ = lazy(() => import("./pages/FAQ"));
const AdminContent = lazy(() => import("./pages/AdminContent"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminPlanos = lazy(() => import("./pages/AdminPlanos"));
const AdminSubscriptions = lazy(() => import("./pages/AdminSubscriptions"));
const AdminReports = lazy(() => import("./pages/AdminReports"));
const AdminAccessLogs = lazy(() => import("./pages/AdminAccessLogs"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const Planos = lazy(() => import("./pages/Planos"));
const TesteVocacional = lazy(() => import("./pages/TesteVocacional"));
const SimuladorEntrevista = lazy(() => import("./pages/SimuladorEntrevista"));
const MeuPerfil = lazy(() => import("./pages/MeuPerfil"));
const Blog = lazy(() => import("./pages/Blog"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/cadastro" element={<Cadastro />} />
              <Route path="/recuperar-senha" element={<ResetPassword />} />
              <Route path="/atualizar-senha" element={<UpdatePassword />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/vagas" element={<Vagas />} />
              <Route path="/vagas/:id" element={<VagaDetalhes />} />
<Route path="/minhas-vagas" element={<MinhasVagas />} />
              <Route path="/candidaturas-recebidas" element={<CandidaturasRecebidas />} />
              <Route path="/minhas-candidaturas" element={<MinhasCandidaturas />} />
              <Route path="/talentos" element={<Talentos />} />
              <Route path="/talentos/:id" element={<TalentoDetalhes />} />
              <Route path="/empresas" element={<Empresas />} />
              <Route path="/empresas/planos" element={<Planos />} />
              <Route path="/buscar" element={<Buscar />} />
              <Route path="/meus-curriculos" element={<MeusCurriculos />} />
              <Route path="/sobre" element={<Sobre />} />
              <Route path="/termos" element={<Termos />} />
              <Route path="/privacidade" element={<Privacidade />} />
              <Route path="/contatos" element={<Contatos />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/content" element={<AdminContent />} />
              <Route path="/admin/contacts" element={<AdminContacts />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/planos" element={<AdminPlanos />} />
              <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
              <Route path="/admin/reports" element={<AdminReports />} />
              <Route path="/admin/access-logs" element={<AdminAccessLogs />} />
              <Route path="/admin/blog" element={<AdminBlog />} />
              <Route path="/teste-vocacional" element={<TesteVocacional />} />
              <Route path="/simulador-entrevista" element={<SimuladorEntrevista />} />
              <Route path="/meu-perfil" element={<MeuPerfil />} />
              <Route path="/blog" element={<Blog />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
